import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { ExportButtons } from '@/components/ExportButtons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface AuditRow {
  id: number;
  user_id: number | null;
  username: string | null;
  full_name: string | null;
  action: string;
  module: string;
  record_id: number | null;
  detail: string | null;
  created_at: string; // 'YYYY-MM-DD HH:MM:SS'
}

interface AuditLogData {
  rows: AuditRow[];
  total: number;
  page: number;
  limit: number;
}

interface AuditLogResponse {
  data: AuditLogData;
}

interface FilterUser {
  id: number;
  username: string;
  full_name: string | null;
  status: string;
}

interface FiltersData {
  modules: string[];
  actions: string[];
  users: FilterUser[];
}

interface FiltersResponse {
  data: FiltersData;
}

// ─── Entity link map ────────────────────────────────────────────────────────────
// Only modules with verified detail routes in App.tsx are listed here.

const ENTITY_LINK_MAP: Record<string, (id: number) => string> = {
  estimates: (id) => `/estimates/${id}`,
  purchase_orders: (id) => `/purchase-orders/${id}`,
  countertop_jobs: (id) => `/countertop-jobs/${id}`,
  returns: (id) => `/returns/${id}`,
  purchase_returns: (id) => `/purchase-returns/${id}`,
  customers: (id) => `/customers/${id}`,
  suppliers: (id) => `/suppliers/${id}`,
  inventory: (id) => `/items/${id}`,
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

const SELECT_CLASS =
  'flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/** Extract HH:MM from a SQLite 'YYYY-MM-DD HH:MM:SS' string */
function extractTime(createdAt: string): string {
  // createdAt is always 'YYYY-MM-DD HH:MM:SS'
  const parts = createdAt.split(' ');
  if (parts.length < 2) return '';
  const timeParts = parts[1].split(':');
  return `${timeParts[0]}:${timeParts[1]}`;
}

// ─── Main page ─────────────────────────────────────────────────────────────────

const DEFAULT_LIMIT = 50;

export default function AuditLogPage() {
  // ── Filter state ──────────────────────────────────────────────────────────────
  const [userId, setUserId] = useState('');
  const [module, setModule] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  // ── Fetch filter options ──────────────────────────────────────────────────────
  const { data: filtersData } = useQuery({
    queryKey: ['audit-log-filters'],
    queryFn: () => apiFetch<FiltersResponse>('/api/audit-log/filters'),
    staleTime: 60_000,
  });

  const filters = filtersData?.data;

  // ── Build query params ────────────────────────────────────────────────────────
  function buildParams(includePage = true): Record<string, string | undefined> {
    return {
      user_id: userId || undefined,
      module: module || undefined,
      action: action || undefined,
      from: from || undefined,
      to: to || undefined,
      q: q || undefined,
      page: includePage ? String(page) : undefined,
      limit: includePage ? String(limit) : undefined,
    };
  }

  // ── Data query ────────────────────────────────────────────────────────────────
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['audit-log', { userId, module, action, from, to, q, page, limit }],
    queryFn: () => {
      const sp = new URLSearchParams();
      const params = buildParams(true);
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined) sp.set(k, v);
      });
      return apiFetch<AuditLogResponse>(`/api/audit-log?${sp.toString()}`);
    },
  });

  const rows = data?.data.rows ?? [];
  const total = data?.data.total ?? 0;

  // ── Filter helpers ────────────────────────────────────────────────────────────
  function resetPage() {
    setPage(1);
  }

  function handleLimitChange(next: number) {
    setLimit(next);
    setPage(1);
  }

  function handleClear() {
    setUserId('');
    setModule('');
    setAction('');
    setFrom('');
    setTo('');
    setQ('');
    setPage(1);
  }

  const hasFilters = !!(userId || module || action || from || to || q);

  return (
    <div className="p-6">
      {/* ── Header ──────────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Immutable record of every action performed in the system.
          </p>
        </div>
        <ExportButtons
          endpoint="/api/audit-log"
          params={{
            user_id: userId || undefined,
            module: module || undefined,
            action: action || undefined,
            from: from || undefined,
            to: to || undefined,
            q: q || undefined,
          }}
        />
      </div>

      {/* ── Filter bar ──────────────────────────────────────────────────────────── */}
      <div className="rounded-lg border bg-muted/20 p-4 mb-5 space-y-3">
        {/* Row 1: dropdowns + search */}
        <div className="flex flex-wrap items-center gap-2">
          {/* User select */}
          <select
            aria-label="Filter by user"
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              resetPage();
            }}
            className={`${SELECT_CLASS} w-44`}
          >
            <option value="">All Users</option>
            {(filters?.users ?? []).map((u) => (
              <option key={u.id} value={String(u.id)}>
                {u.username}
                {u.status !== 'active' ? ' (inactive)' : ''}
              </option>
            ))}
          </select>

          {/* Module select */}
          <select
            aria-label="Filter by module"
            value={module}
            onChange={(e) => {
              setModule(e.target.value);
              resetPage();
            }}
            className={`${SELECT_CLASS} w-44`}
          >
            <option value="">All Modules</option>
            {(filters?.modules ?? []).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          {/* Action select */}
          <select
            aria-label="Filter by action"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              resetPage();
            }}
            className={`${SELECT_CLASS} w-52`}
          >
            <option value="">All Actions</option>
            {(filters?.actions ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>

          {/* Free-text search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              aria-label="Search detail or record ID"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                resetPage();
              }}
              placeholder="Search detail or record ID…"
              className="pl-8 h-9"
            />
          </div>
        </div>

        {/* Row 2: date range + clear */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">From</span>
          <Input
            aria-label="Filter from date"
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              resetPage();
            }}
            className="w-36 h-8 text-sm"
          />
          <span className="text-sm text-muted-foreground">To</span>
          <Input
            aria-label="Filter to date"
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              resetPage();
            }}
            className="w-36 h-8 text-sm"
          />
          {hasFilters && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="h-8 px-2 text-xs text-muted-foreground gap-1"
            >
              <X className="h-3 w-3" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* ── Loading / error ──────────────────────────────────────────────────────── */}
      {isLoading && <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
      {isError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive mb-4">
          {error instanceof Error ? error.message : 'Failed to load audit log'}
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────────────────────── */}
      {!isLoading && (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground w-32">When</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground w-28">User</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground w-44">
                  Action
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground w-32">
                  Module
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground w-20">
                  Record
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                    No audit entries match these filters.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const linkBuilder =
                  row.record_id !== null ? ENTITY_LINK_MAP[row.module] : undefined;

                return (
                  <tr key={row.id} className="hover:bg-muted/30 transition-colors">
                    {/* When */}
                    <td className="px-4 py-3 align-top">
                      <span className="block text-xs text-foreground tabular-nums">
                        {formatDate(row.created_at)}
                      </span>
                      <span className="block text-xs font-mono text-muted-foreground tabular-nums">
                        {extractTime(row.created_at)}
                      </span>
                    </td>

                    {/* User */}
                    <td className="px-4 py-3 align-top font-mono text-xs text-muted-foreground">
                      {row.username ?? '—'}
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3 align-top">
                      <Badge variant="gray" className="font-mono text-xs">
                        {row.action}
                      </Badge>
                    </td>

                    {/* Module */}
                    <td className="px-4 py-3 align-top text-xs text-muted-foreground font-mono">
                      {row.module}
                    </td>

                    {/* Record */}
                    <td className="px-4 py-3 align-top text-xs tabular-nums">
                      {row.record_id === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : linkBuilder ? (
                        <Link
                          to={linkBuilder(row.record_id)}
                          className="text-primary hover:underline underline-offset-4"
                        >
                          {row.record_id}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">{row.record_id}</span>
                      )}
                    </td>

                    {/* Detail */}
                    <td className="px-4 py-3 align-top max-w-[280px]">
                      {row.detail ? (
                        <span
                          className="block text-xs text-muted-foreground truncate"
                          title={row.detail}
                        >
                          {row.detail}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ───────────────────────────────────────────────────────────── */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={handleLimitChange}
        />
      )}
    </div>
  );
}
